import { useEffect, useRef, useState } from 'react';
import type { ModalType } from '../registry/types';
import { getModalFromQuery, getWebviewModalFromQuery } from 'app/shared/utils/modalUtils';
import { preloadModalChunk } from '../registry';

/**
 * URL Query Parameter to Modal Type Mapping
 *
 * Maps query parameter values to internal modal types for URL-based modal triggering.
 * The lookup is case-insensitive to support various URL formats.
 *
 * Supports multiple query parameter patterns:
 * - ?modal=contact          → opens 'contact' modal
 * - ?showModal=promptLogin  → opens 'promptLogin' modal
 * - ?webviewModal=Contact   → opens 'contact' modal in webview mode (no close button)
 * - ?contactModal=true      → legacy boolean flag, opens contact modal (triggered via email link)
 * - ?instantTour=true       → legacy boolean flag, handled separately by existing InstantTour system (mobile app webview)
 *
 * Examples:
 * - `/listing/123?modal=contact` → triggers contact modal with close button
 * - `/search?showModal=createSearchAlert` → triggers search alert modal
 * - `/hdp/456?webviewModal=Contact` → triggers contact modal without close button (webview)
 * - `/listing/789?contactModal=true` → legacy format used by email links
 *
 * Integration with modalUtils.ts:
 * This hook integrates with existing modalUtils.ts functions (getModalFromQuery,
 * getWebviewModalFromQuery) to detect modals. The modalUtils functions handle
 * capitalized modal IDs while this mapping handles the normalization to internal types.
 */

// Primary mapping - single source of truth for modal mappings
const modalMappings: Record<string, ModalType> = {
  contact: 'contact',
  costsandfees: 'costsAndFees',
  postcontactrenterprofile: 'postContactRenterProfile',
  postcontactsavedsearch: 'postContactSavedSearch',
  contactsuccess: 'contactSuccess',
  promptlogin: 'promptLogin',

  // Future modal types - uncomment as they get migrated to ModalContext
  // promptfeedback: 'promptFeedback',
  // createsearchalert: 'createSearchAlert',
  // deletesearchalert: 'deleteSearchAlert',
};

// Helper function for case-insensitive lookups
function getModalMapping(key: string): ModalType | undefined {
  // Normalize the key to lowercase for lookup
  const normalizedKey = key.toLowerCase();
  return modalMappings[normalizedKey];
}

// Build the URL_MODAL_MAPPING for backward compatibility
// This preserves the exact keys that modalUtils.ts might return (capitalized)
const URL_MODAL_MAPPING: Record<string, ModalType> = {
  // Add capitalized versions for backward compatibility
  Contact: 'contact',
  CostsAndFees: 'costsAndFees',

  // Include all lowercase mappings
  ...modalMappings,
};

/**
 * Hook that automatically opens modals based on URL query parameters.
 *
 * Features:
 * - Detects and opens modals from URL parameters
 * - Handles webview mode (hides close button for native app integration)
 * - Prevents duplicate modal opens for the same URL
 * - Cleans up URL when modal closes
 * - Tracks modal state to prevent reopening issues
 *
 * Supported URL patterns:
 * - ?modal=contact - Standard modal with close button
 * - ?showModal=promptLogin - Webview mode (no close button)
 * - ?webviewModal=Contact - Webview mode (no close button)
 * - ?contactModal=true - Legacy boolean flag (triggered via email link)
 * - ?instantTour=true - Legacy boolean flag (handled by existing InstantTour system (mobile app webview))
 *
 * State Management:
 * - Uses modalIdentifier to track unique modal instances (includes webview state)
 * - isProgrammaticChange flag prevents reopening when URL changes from modal close
 * - Handles browser back/forward navigation correctly
 *
 * ⚠️  IMPORTANT: Disabled by default to avoid conflicts with existing modal handling.
 * Enable explicitly in components that need URL-triggered modals via ModalContext.
 *
 * @param enabled - Whether to enable URL-based modal triggering (default: false)
 * @param additionalProps - Extra props to pass to the modal when opened
 * @param dependencies - Object containing modal control functions and optional location
 * @param dependencies.openModal - Function to open a modal (from useModal hook)
 * @param dependencies.closeModalByType - Function to close a modal by type
 * @param dependencies.location - Optional location object (for react-router integration)
 */
export const useUrlModalTrigger = (
  enabled = false, // Default to false to prevent conflicts with existing modal handling
  additionalProps?: Record<string, any>,
  dependencies?: {
    openModal: (config: { type: ModalType; props?: any; onClose?: () => void }) => string;
    closeModalByType: (type: ModalType) => void;
    location?: { search: string; pathname: string; hash: string };
  },
) => {
  // Track current URL search params to detect changes
  const [currentSearch, setCurrentSearch] = useState<string>(() =>
    typeof window !== 'undefined' ? window.location.search : '',
  );

  // Track both the processed modal and whether it's currently open
  const [modalState, setModalState] = useState<{
    processedParam: string | null;
    isOpen: boolean;
    isProgrammaticChange: boolean;
  }>({
    processedParam: null,
    isOpen: false,
    isProgrammaticChange: false,
  });

  // Use a ref to access modalState in the effect without causing re-renders
  const modalStateRef = useRef(modalState);
  modalStateRef.current = modalState;

  // Track the currently open modal type for cleanup
  const openModalTypeRef = useRef<ModalType | null>(null);

  // Listen for URL changes (back/forward navigation, manual URL changes)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleLocationChange = () => {
      const newSearch = window.location.search;
      // Only update if search actually changed to prevent unnecessary re-renders
      if (newSearch !== currentSearch) {
        // If this was a programmatic change (from modal close), reset the flag
        if (modalState.isProgrammaticChange) {
          setModalState((prev) => ({ ...prev, isProgrammaticChange: false }));
        }
        setCurrentSearch(newSearch);
      }
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);

    // Check for manual URL changes periodically (only update if different)
    const interval = setInterval(handleLocationChange, 100);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      clearInterval(interval);
    };
  }, [currentSearch, modalState.isProgrammaticChange]);

  useEffect(() => {
    if (!enabled || !dependencies) {
      return;
    }

    const { openModal, closeModalByType } = dependencies;

    // Use the reactive currentSearch state instead of static location object
    const search = currentSearch;
    const params = new URLSearchParams(search);

    // Convert URLSearchParams to a plain object for modalUtils functions
    const queryObject: Record<string, string | boolean | undefined> = {};
    params.forEach((value, key) => {
      // Convert string "true"/"false" to boolean for legacy boolean flags
      // Empty values (like ?contactModal without =value) become true
      const convertedValue = value === 'true' ? true : value === 'false' ? false : value || true;
      queryObject[key] = convertedValue;
    });

    // Use modalUtils.ts functions to detect modals (they handle capitalized IDs)
    // Check for explicit webview parameters
    const hasWebviewParam = params.has('webviewModal') || params.has('showModal');

    // Legacy boolean flags:
    // - instantTour=true should be treated as a webview modal (no close button)
    // - contactModal=true should be treated as a regular modal (with close button)
    const hasLegacyWebviewFlag = queryObject.instantTour === true;

    // Call the appropriate detection function
    // For legacy webview flags, use webview detection
    let webviewModalId: string | null = null;
    let regularModalId: string | null = null;

    if (hasWebviewParam || hasLegacyWebviewFlag) {
      // Use webview detection for explicit webviewModal/showModal params or instantTour=true
      webviewModalId = getWebviewModalFromQuery(queryObject);
    } else {
      // Use regular detection for modal parameter and other legacy flags like contactModal=true
      regularModalId = getModalFromQuery(queryObject);
    }

    // Determine which modal to open and whether it's a webview
    const modalIdFromUtils = webviewModalId || regularModalId;
    // Determine if this should be treated as a webview modal (no close button, typically for native apps)
    // It's a webview if: explicit webviewModal/showModal param, or instantTour=true legacy flag
    const isWebview = hasWebviewParam || (hasLegacyWebviewFlag && webviewModalId !== null);

    // Also check for our own lowercase mappings
    const modalParam = params.get('modal') || params.get('showModal') || params.get('webviewModal');
    const normalizedModalParam = modalParam?.toLowerCase();

    // Try to find the modal type from either modalUtils result or our lowercase mapping
    let modalType: ModalType | undefined;
    let modalIdentifier: string | undefined;

    if (modalIdFromUtils) {
      // Modal detected by modalUtils.ts (typically capitalized format like 'Contact')
      modalType = getModalMapping(modalIdFromUtils);
      if (modalType) {
        // Include webview state in identifier to track separately
        // This prevents reopening the same modal if only the webview state changes
        modalIdentifier = isWebview ? `webview:${modalIdFromUtils}` : modalIdFromUtils;
      }
    } else if (normalizedModalParam) {
      // Check our mapping with normalized (lowercase) parameter
      modalType = getModalMapping(normalizedModalParam);
      if (modalType) {
        // Include webview state in identifier to track separately
        // Legacy boolean flags are also treated as webview modals
        modalIdentifier =
          hasWebviewParam || hasLegacyWebviewFlag ? `webview:${normalizedModalParam}` : normalizedModalParam;
      }
    }

    if (modalType && modalIdentifier) {
      const currentModalState = modalStateRef.current;

      // Prevent reopening the same modal multiple times
      // Skip if: 1) We've already processed this exact modal and it's still open
      //         2) We just closed a modal programmatically (user clicked close, not browser navigation)
      if (
        (currentModalState.processedParam === modalIdentifier && currentModalState.isOpen) ||
        currentModalState.isProgrammaticChange
      ) {
        return;
      }

      // Function to open the modal
      const performOpenModal = () => {
        // Mark modal as open
        setModalState({
          processedParam: modalIdentifier,
          isOpen: true,
          isProgrammaticChange: false,
        });

        // Track the open modal type for cleanup
        openModalTypeRef.current = modalType;

        openModal({
          type: modalType,
          props: {
            ...additionalProps,
            fromUrl: true,
            isWebview, // Note: Using lowercase 'v' for consistency with modal system
            useCloseButton: !isWebview, // Hide close button in webview mode (for native apps)
            clientLoaded: true, // Force clientLoaded to prevent form render delays
            isCachedDataPending: false, // Skip cache loading check for URL-triggered modals
          },
          onClose: () => {
            // Mark modal as closing programmatically before changing URL
            // This flag prevents the modal from reopening when we remove the URL parameter
            setModalState({
              processedParam: modalIdentifier,
              isOpen: false,
              isProgrammaticChange: true, // Flag to prevent reopening on URL change
            });

            // Remove modal parameter from URL when closed
            if (typeof window === 'undefined') return;

            const newParams = new URLSearchParams(window.location.search);
            newParams.delete('modal');
            newParams.delete('showModal');
            newParams.delete('webviewModal');
            // Also remove legacy boolean flags
            newParams.delete('contactModal');
            newParams.delete('instantTour');

            const newSearch = newParams.toString();
            const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;

            // Update URL without triggering navigation
            window.history.replaceState({}, '', newUrl);
          },
        });
      };

      // Preload the modal chunk before opening to prevent lazy-loading pop-in
      // Note: For modals already preloaded (e.g., contact on pad/building pages), this will be instant
      const openModalAsync = async () => {
        await preloadModalChunk(modalType);
        performOpenModal();
      };
      openModalAsync();
    } else if (!modalType && openModalTypeRef.current) {
      // No modal parameter in URL but we have a modal open
      // This happens when user navigates back or manually removes the parameter
      // Only close if not already closing programmatically (prevents double-close)
      if (!modalStateRef.current.isProgrammaticChange) {
        closeModalByType(openModalTypeRef.current);
      }

      openModalTypeRef.current = null;

      // Reset state
      setModalState({
        processedParam: null,
        isOpen: false,
        isProgrammaticChange: false,
      });
    }
  }, [enabled, currentSearch, dependencies?.openModal, dependencies?.closeModalByType, additionalProps]);
};

/**
 * Utility function to generate URLs with modal parameters.
 *
 * Creates a URL that will trigger a modal when navigated to.
 * Uses the standard 'modal' query parameter for consistency.
 *
 * @param baseUrl - The base URL to append the modal parameter to
 * @param modalType - The modal type key from URL_MODAL_MAPPING (e.g., 'contact', 'Contact')
 * @returns Full URL with modal parameter appended
 *
 * @example
 * generateModalUrl('/listing/123', 'contact')
 * // Returns: '/listing/123?modal=contact'
 */
export const generateModalUrl = (baseUrl: string, modalType: keyof typeof URL_MODAL_MAPPING): string => {
  if (typeof window === 'undefined') return baseUrl;

  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('modal', modalType);
  return url.toString();
};

/**
 * Hook to get the current modal type from URL parameters.
 *
 * Checks for modal parameters in the current URL and returns the
 * corresponding modal type if found. Useful for conditional rendering
 * or tracking which modal is currently triggered by URL.
 *
 * @param location - Optional location object (for react-router integration)
 *                   If not provided, uses window.location
 * @returns The modal type from URL or null if no modal parameter found
 *
 * @example
 * const modalType = useModalFromUrl();
 * if (modalType === 'contact') {
 *   // Contact modal is triggered by URL
 * }
 */
export const useModalFromUrl = (location?: { search: string }): ModalType | null => {
  const search = location?.search || (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(search);
  const modalParam = params.get('modal') || params.get('showModal') || params.get('webviewModal');

  if (modalParam) {
    // Use case-insensitive lookup
    return getModalMapping(modalParam) || null;
  }

  return null;
};

/**
 * Dynamically add a new URL parameter to modal type mapping.
 *
 * Allows runtime registration of new modal URL patterns.
 * Useful for feature flags or dynamically loaded modal types.
 * Both lowercase and capitalized versions should be added for consistency.
 *
 * @param urlKey - The URL parameter value to map (e.g., 'feedback', 'Feedback')
 * @param modalType - The internal modal type to map to (from ModalType union)
 *
 * @example
 * // Add support for ?modal=feedback
 * addUrlModalMapping('feedback', 'promptFeedback');
 * addUrlModalMapping('Feedback', 'promptFeedback'); // For backward compatibility
 */
export const addUrlModalMapping = (urlKey: string, modalType: ModalType): void => {
  // Add to primary mapping (normalized to lowercase)
  modalMappings[urlKey.toLowerCase()] = modalType;
  // Also add the exact key for backward compatibility
  URL_MODAL_MAPPING[urlKey] = modalType;
};
